/**
 * Права пользователя и решения по ним (ТЗ §3, §5.1).
 *
 * Клиент получает весь набор прав одним запросом на входе и решает локально, что
 * показывать. Спрашивать сервер про каждый пункт меню значило бы десятки кругов при
 * каждом входе и меню, которое собирается на глазах.
 *
 * Важно, чего эта проверка НЕ делает: она не защищает данные. Скрытый пункт меню — это
 * удобство, а не безопасность; запрет живёт на сервере в `@PreAuthorize` и предикатах
 * области видимости (ADR-0007). Клиент, обошедший это меню руками, получит 403.
 */

/** Область видимости права: чьи записи доступны. */
export type Scope = 'all' | 'department' | 'own' | 'denied'

/** Действие над ресурсом. */
export type Action = 'create' | 'read' | 'update' | 'delete' | 'approve' | 'export' | 'use'

/** Ответ `/api/v1/me`. */
export interface CurrentUser {
  userId: string | null
  employeeId: string | null
  roles: string[]
  superadmin: boolean
  /** `resource -> { action: scope }` */
  permissions: Record<string, Record<string, string>>
  /** `resource -> { "action:field": scope }` */
  fieldPermissions: Record<string, Record<string, string>>
}

/**
 * Может ли пользователь выполнить действие над ресурсом.
 *
 * Отсутствие права — это запрет, а не «неограниченно». Обратное прочтение — классический
 * способ показать чужой раздел: право не нашлось, значит ограничений нет.
 */
export function can(user: CurrentUser | null, resource: string, action: Action): boolean {
  if (!user) return false
  // Суперадмин обходит проверку намеренно: на сервере у него все гранты уже ALL,
  // и требовать здесь явной записи значило бы дублировать эту логику с риском разойтись.
  if (user.superadmin) return true
  return Boolean(user.permissions[resource]?.[action])
}

/**
 * Область видимости права.
 *
 * Возвращает `denied`, когда права нет вовсе. Списочный экран должен различать «читаю
 * всё» и «читаю своё» — это разные запросы к серверу, а не разное оформление.
 */
export function scopeOf(user: CurrentUser | null, resource: string, action: Action): Scope {
  if (!user) return 'denied'
  if (user.superadmin) return 'all'
  const scope = user.permissions[resource]?.[action]
  return (scope as Scope) ?? 'denied'
}

/**
 * Может ли пользователь видеть конкретное поле.
 *
 * Требует явного гранта на поле: право читать карточку сотрудника не даёт права видеть
 * оклад. Ровно в этом смысл полевых прав из ТЗ §3 — иначе оклад увидит каждый, кто может
 * открыть карточку.
 */
export function canField(
  user: CurrentUser | null,
  resource: string,
  action: Action,
  field: string,
): boolean {
  if (!user) return false
  if (user.superadmin) return true
  return Boolean(user.fieldPermissions[resource]?.[`${action}:${field}`])
}

/** Есть ли у пользователя роль. */
export function hasRole(user: CurrentUser | null, role: string): boolean {
  return Boolean(user?.roles.includes(role))
}
